import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import NewDeckAgent from './new-deck/NewDeckAgent.tsx';
import NewDeckBlank from './new-deck/NewDeckBlank.tsx';

/**
 * New deck = the same canvas, empty, with the structured agent intake behind a
 * one-shot probe of the build engines:
 *
 * - **Agent flow** (`<NewDeckAgent>`, default when ANY engine is free): drop in
 *   a brief + images, answer ~6 questions, then the server spawns the build
 *   agent to plan/scaffold/write every slide headlessly, streaming progress
 *   live. Which engine runs it is the wizard's own Build engine row.
 * - **Blank start** (`<NewDeckBlank>`): cover + thank-you only, no planner or
 *   agent — reachable from the agent flow, and the fallback when no engine can
 *   start right now.
 *
 * ANY ENGINE, NOT CLAUDE. Until 2026-08-04 this probed `/claude-status` and
 * required Claude specifically, so a server running deliberately on OpenCode, ⚙
 * "Offer Claude" off with OpenCode configured and ready, was told "No build
 * engine is available" and never got the wizard, which is the only place the
 * Build engine row exists. OpenCode was unreachable BECAUSE it was the only engine. Anything
 * added here that asks "can we build" asks the server, which asks all three.
 *
 * This shell owns only the probe. Once it resolves it mounts exactly one child.
 */

/** null while the probe is in flight — that is the "Setting up…" state, not a
 *  missing value. */
type Flow = null | 'agent' | 'blank' | 'unavailable';

/** The fields of `/api/wizard/build-status` this shell reads. Kept narrow
 *  deliberately: the `.catch` below answers with exactly this literal, so a
 *  wider type would make the fallback the thing that fails.
 *
 *  `engines` is keyed by engine id ('claude' | 'fast' | 'pi'), and each
 *  entry's `reason` is an already-user-facing sentence from the server — this
 *  screen is a dead end without them for the one person who could fix it. */
type BuildStatus = {
  ready?: boolean;
  busy?: boolean;
  engines?: Record<string, { available?: boolean; reason?: string } | null | undefined>;
};
export default function NewDeck() {
  // null = probing; 'agent' = agent flow; 'blank' = blank start;
  // 'unavailable' = no build engine can start now (message + blank offer).
  const [flow, setFlow] = useState<Flow>(null);
  // Why each engine can't start, when none can — already user-facing sentences
  // from the server. Without them this screen is a dead end for the one person
  // who could fix it.
  const [engineReasons, setEngineReasons] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<BuildStatus>('/api/wizard/build-status')
      .catch((): BuildStatus => ({ ready: false, busy: false, engines: {} }))
      .then((st) => {
        if (cancelled) return;
        // `ready` ONLY — never `busy`. `busy` means every build slot is in use,
        // and gating the wizard on it meant a full builder refused to render
        // the intake form at all: you could not so much as fill in a brief
        // while someone else's deck built, and the copy still claimed "a deck
        // is being built" as though one were the maximum. Capacity is now a
        // footer hint on the form (`slots` on NewDeckModal) and the server's
        // 409 is the real gate.
        if (st.ready) {
          setFlow('agent');
          return;
        }
        setEngineReasons(
          Object.entries(st.engines || {})
            .filter(([, e]) => e && !e.available && e.reason)
            .map(([id, e]) => `${id[0]?.toUpperCase()}${id.slice(1)}: ${e?.reason}`),
        );
        setFlow('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (flow === null) {
    return (
      <div className="editor editor--new">
        <div className="editor__stage">
          <main className="preview-frame preview-frame--empty">
            <div className="editor__blank">
              <span className="editor__blank-mark" aria-hidden="true" />
              <p className="muted">Setting up…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (flow === 'blank') {
    return <NewDeckBlank />;
  }

  if (flow === 'unavailable') {
    return (
      <div className="editor editor--new">
        <div className="editor__stage">
          <main className="preview-frame preview-frame--empty">
            <div className="editor__blank">
              <span className="editor__blank-mark" aria-hidden="true" />
              <p className="muted">
                No build engine is available right now. Turn one on in Settings, or start a blank deck and add slides
                yourself.
              </p>
              {engineReasons.length > 0 && (
                <ul className="editor__blank-reasons">
                  {engineReasons.map((reason) => (
                    <li key={reason} className="muted">
                      {reason}
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" className="chip" onClick={() => setFlow('blank')}>
                Start a blank deck
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return <NewDeckAgent onStartBlank={() => setFlow('blank')} />;
}
